import convert from "convert-units";

/**
 * Checks if a unit is already metric
 * @param {string} unit - The unit to check
 * @returns {boolean} - True if the unit is already metric
 */
const isMetricUnit = (unit) => {
  if (!unit) return false;
  
  const unitLower = unit.toLowerCase().trim();
  const metricUnits = [
    "ml", "milliliter", "milliliters",
    "l", "liter", "liters",
    "g", "gram", "grams",
    "kg", "kilogram", "kilograms",
    "°c", "c", "celsius"
  ];
  
  return metricUnits.includes(unitLower);
};

/**
 * Converts ingredient measurements to metric equivalents
 * @param {number} amount - The amount to convert
 * @param {string} unit - The unit to convert from
 * @returns {Object} - Object with metricAmount and metricUnit, or null if no conversion
 */
export const convertToMetric = (amount, unit) => {
  if (!amount || !unit || unit === "none") {
    return null;
  }

  // Convert amount to number if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Validate numeric amount
  if (isNaN(numericAmount) || numericAmount <= 0) {
    return null;
  }

  // Don't convert if already metric
  if (isMetricUnit(unit)) {
    return null;
  }

  // Normalize the unit for convert-units compatibility
  const normalizedUnit = normalizeUnit(unit);
  
  if (!normalizedUnit) {
    return null;
  }

  try {
    // Check if the unit can be converted
    const availableUnits = convert().list();
    const unitExists = availableUnits.some(u => 
      u.abbr === normalizedUnit || 
      u.singular === normalizedUnit || 
      u.plural === normalizedUnit
    );

    if (!unitExists) {
      return null;
    }

    // Try to convert to metric equivalent
    let metricAmount, metricUnit;

    // Volume conversions
    if (convert().describe(normalizedUnit).measure === "volume") {
      // Convert to milliliters first, then to appropriate metric unit
      const ml = convert(numericAmount).from(normalizedUnit).to("ml");
      
      if (ml >= 1000) {
        metricAmount = Math.round(ml / 1000 * 10) / 10; // Round to 1 decimal
        metricUnit = "L";
      } else {
        metricAmount = Math.round(ml);
        metricUnit = "ml";
      }
    }
    // Mass/Weight conversions
    else if (convert().describe(normalizedUnit).measure === "mass") {
      // Convert to grams first, then to appropriate metric unit
      const grams = convert(numericAmount).from(normalizedUnit).to("g");
      
      if (grams >= 1000) {
        metricAmount = Math.round(grams / 1000 * 10) / 10; // Round to 1 decimal
        metricUnit = "kg";
      } else {
        metricAmount = Math.round(grams);
        metricUnit = "g";
      }
    }
    // Temperature conversions
    else if (convert().describe(normalizedUnit).measure === "temperature") {
      if (normalizedUnit === "F") {
        metricAmount = Math.round(convert(numericAmount).from("F").to("C"));
        metricUnit = "°C";
      }
    }
    else {
      return null;
    }

    return { metricAmount, metricUnit };
  } catch (error) {
    console.warn("Unit conversion failed:", error);
    return null;
  }
};

/**
 * Normalizes unit strings to be compatible with convert-units library
 * @param {string} unit - The unit string to normalize
 * @returns {string|null} - Normalized unit or null if not convertible
 */
const normalizeUnit = (unit) => {
  if (!unit) return null;
  
  const unitLower = unit.toLowerCase().trim();
  
  // Volume unit mappings
  const volumeUnits = {
    "cup": "cup",
    "cups": "cup",
    "c": "cup",
    "tablespoon": "Tbs",
    "tablespoons": "Tbs",
    "tbsp": "Tbs",
    "tbs": "Tbs",
    "teaspoon": "tsp",
    "teaspoons": "tsp",
    "tsp": "tsp",
    "fluid ounce": "fl-oz",
    "fluid ounces": "fl-oz",
    "fl oz": "fl-oz",
    "fl-oz": "fl-oz",
    "floz": "fl-oz",
    "pint": "pnt",
    "pints": "pnt",
    "pt": "pnt",
    "quart": "qt",
    "quarts": "qt",
    "qt": "qt",
    "gallon": "gal",
    "gallons": "gal",
    "gal": "gal",
    "liter": "l",
    "liters": "l",
    "l": "l",
    "milliliter": "ml",
    "milliliters": "ml",
    "ml": "ml",
  };

  // Mass/Weight unit mappings
  const massUnits = {
    "ounce": "oz",
    "ounces": "oz",
    "oz": "oz",
    "pound": "lb",
    "pounds": "lb",
    "lb": "lb",
    "lbs": "lb",
    "gram": "g",
    "grams": "g",
    "g": "g",
    "kilogram": "kg",
    "kilograms": "kg",
    "kg": "kg",
  };

  // Temperature unit mappings
  const temperatureUnits = {
    "fahrenheit": "F",
    "f": "F",
    "°f": "F",
    "celsius": "C",
    "c": "C",
    "°c": "C",
  };

  // Check volume units first
  if (volumeUnits[unitLower]) {
    return volumeUnits[unitLower];
  }
  
  // Check mass units
  if (massUnits[unitLower]) {
    return massUnits[unitLower];
  }
  
  // Check temperature units
  if (temperatureUnits[unitLower]) {
    return temperatureUnits[unitLower];
  }

  return null;
};

/**
 * Formats an ingredient with metric conversion in parentheses
 * @param {number} originalAmount - The original numeric amount for conversion
 * @param {string} unit - The unit
 * @param {string} name - The ingredient name
 * @param {string} displayAmount - The formatted amount for display (optional, defaults to originalAmount)
 * @returns {string} - Formatted ingredient string with metric conversion
 */
export const formatIngredientWithMetric = (originalAmount, unit, name, displayAmount = null) => {
  const amountToShow = displayAmount || originalAmount;
  let ingredientText = "";
  
  // Format the original amount and unit
  if (amountToShow && unit && unit !== "none") {
    ingredientText = `${amountToShow} ${unit} ${name}`.trim();
  } else if (amountToShow) {
    ingredientText = `${amountToShow} ${name}`.trim();
  } else {
    ingredientText = name;
  }

  // Add metric conversion if available (use original numeric amount for conversion)
  const metricConversion = convertToMetric(originalAmount, unit);
  if (metricConversion) {
    const { metricAmount, metricUnit } = metricConversion;
    ingredientText += ` (${metricAmount}${metricUnit})`;
  }

  return ingredientText;
};